import { API_URL, TIMEOUTS } from '../constants/config';
import { logger } from './logger';
import { authAPI } from '../api/auth';

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
  timestamp: number;
}

export interface HealthCheckResult {
  status: string;
  environment: string;
  version: string;
  database_status?: string;
}

/**
 * Test basic connectivity to the backend
 */
export const testBackendConnection =
  async (): Promise<ConnectionTestResult> => {
    const startTime = Date.now();

    try {
      // Derive backend URL from API_URL
      const backendUrl = API_URL.replace('/api/v1', '');
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        TIMEOUTS.API_REQUEST
      );

      const response = await fetch(backendUrl, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const duration = Date.now() - startTime;

      return {
        success: response.ok,
        message: response.ok
          ? `Backend accessible (${duration}ms)`
          : `Backend returned ${response.status}: ${response.statusText}`,
        details: {
          status: response.status,
          statusText: response.statusText,
          duration,
          url: backendUrl,
        },
        timestamp: Date.now(),
      };
    } catch (error) {
      const duration = Date.now() - startTime;

      return {
        success: false,
        message:
          error instanceof Error ? error.message : 'Unknown connection error',
        details: {
          error: error instanceof Error ? error.name : 'UnknownError',
          duration,
          url: API_URL.replace('/api/v1', ''),
        },
        timestamp: Date.now(),
      };
    }
  };

/**
 * Test API health endpoint
 */
export const testAPIHealth = async (): Promise<ConnectionTestResult> => {
  const startTime = Date.now();

  try {
    const healthData = await authAPI.healthCheck();
    const duration = Date.now() - startTime;

    return {
      success: true,
      message: `API health check passed (${duration}ms)`,
      details: {
        ...healthData,
        duration,
        url: `${API_URL.replace('/api/v1', '')}/health`,
      },
      timestamp: Date.now(),
    };
  } catch (error) {
    const duration = Date.now() - startTime;

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Health check failed',
      details: {
        error: error instanceof Error ? error.name : 'UnknownError',
        duration,
        url: `${API_URL.replace('/api/v1', '')}/health`,
      },
      timestamp: Date.now(),
    };
  }
};

/**
 * Test authentication endpoints
 */
export const testAuthEndpoints = async (): Promise<ConnectionTestResult> => {
  const startTime = Date.now();

  try {
    // Try to access a protected endpoint without auth (should fail with 401)
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIMEOUTS.API_REQUEST
    );

    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    // We expect a 401 for unauthenticated requests
    const isExpectedResponse = response.status === 401;

    return {
      success: isExpectedResponse,
      message: isExpectedResponse
        ? `Auth endpoints working correctly (${duration}ms)`
        : `Unexpected auth response: ${response.status}`,
      details: {
        status: response.status,
        statusText: response.statusText,
        duration,
        url: `${API_URL}/auth/me`,
      },
      timestamp: Date.now(),
    };
  } catch (error) {
    const duration = Date.now() - startTime;

    return {
      success: false,
      message:
        error instanceof Error ? error.message : 'Auth endpoints test failed',
      details: {
        error: error instanceof Error ? error.name : 'UnknownError',
        duration,
        url: `${API_URL}/auth/me`,
      },
      timestamp: Date.now(),
    };
  }
};

/**
 * Comprehensive connection test suite
 */
export const runFullConnectionTest = async (): Promise<{
  overall: boolean;
  results: {
    backend: ConnectionTestResult;
    health: ConnectionTestResult;
    auth: ConnectionTestResult;
  };
  summary: string;
}> => {
  logger.log('Running connection tests...');

  const [backendResult, healthResult, authResult] = await Promise.allSettled([
    testBackendConnection(),
    testAPIHealth(),
    testAuthEndpoints(),
  ]);

  const backend =
    backendResult.status === 'fulfilled'
      ? backendResult.value
      : {
          success: false,
          message: 'Backend test failed to run',
          timestamp: Date.now(),
        };

  const health =
    healthResult.status === 'fulfilled'
      ? healthResult.value
      : {
          success: false,
          message: 'Health test failed to run',
          timestamp: Date.now(),
        };

  const auth =
    authResult.status === 'fulfilled'
      ? authResult.value
      : {
          success: false,
          message: 'Auth test failed to run',
          timestamp: Date.now(),
        };

  const allPassed = backend.success && health.success && auth.success;
  const passedCount = [backend.success, health.success, auth.success].filter(
    Boolean
  ).length;

  const summary = allPassed
    ? 'All connection tests passed'
    : `${passedCount}/3 tests passed`;

  const results = { backend, health, auth };

  logger.log('Connection test:', allPassed ? 'OK' : `${passedCount}/3 passed`);

  return {
    overall: allPassed,
    results,
    summary,
  };
};
